import { createContext, useContext } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// UTILS
import {
  GENERIC_OUTPUT_FIRST,
  GENERIC_OUTPUT_LAST,
  takesOutputNumber,
} from "../../../utils/controlFloorRules";

/**
 * Set by the action card when its row needs a floor but has none to offer — a
 * ShuttleFloor row whose bank's floors have not loaded, or are all out of the
 * operator's scope. Its buttons are blocked, and "select a floor" would point at
 * a strip that is not there. Defaults to false, so a button rendered anywhere
 * else reads the ordinary reason.
 */
export const NoFloorAvailableContext = createContext(false);

/**
 * Why a row's buttons are waiting, named for what they are waiting ON.
 *
 * `floorSelectionMissing` holds a row's buttons until its target is chosen. For
 * most positional rows that target is a floor, picked on the row's strip. A
 * GenericOutput row's target is an output number entered in its own field, and
 * telling that operator to "select a floor" points at a strip that is not there
 * — as it does on a row that has no floor to offer at all.
 *
 * `commandEnum` is the row's primary command; a row's paired command goes to the
 * same target, so one reason covers both of its buttons.
 */
export default function useAwaitingTargetReason(commandEnum: number | null): string {
  const [t] = useTranslation("global");
  const noFloorAvailable = useContext(NoFloorAvailableContext);

  if (takesOutputNumber(commandEnum)) {
    return t(
      "monitoringActions.enterOutputFirst",
      "Enter an output number ({{first}}-{{last}}) for this command first",
      { first: GENERIC_OUTPUT_FIRST, last: GENERIC_OUTPUT_LAST },
    );
  }

  if (noFloorAvailable) {
    return t(
      "monitoringActions.noFloorAvailable",
      "No floor is available to send this command to, so it cannot be sent. The bank's floors may still be loading, or none is in your scope.",
    );
  }

  return t("monitoringActions.selectFloorFirst", "Select a floor for this command first");
}
